/**
 * Sticky Add-to-Cart Bar
 * Appears when user scrolls past the main ATC button on PDP
 * Syncs with main form's variant selection and quantity picker
 */
class StickyATCBar {
  constructor(container) {
    this.bar = container;
    this.form = container.querySelector('[data-sticky-atc-form]');
    this.variantInput = container.querySelector('[data-sticky-variant-id]');
    this.quantityInput = container.querySelector('[data-sticky-quantity]');
    this.metresInput = container.querySelector('[data-sticky-metres]');
    this.button = container.querySelector('[data-sticky-atc-button]');
    this.priceDisplay = container.querySelector('[data-sticky-price]');
    this.variantDisplay = container.querySelector('[data-sticky-variant-display]');
    this.buttonText = container.querySelector('[data-sticky-button-text]');

    // Quantity picker elements
    this.qtyPicker = container.querySelector('[data-sticky-quantity-picker]');
    this.qtyValue = container.querySelector('[data-sticky-qty-value]');
    this.qtyUnit = container.querySelector('[data-sticky-qty-unit]');
    this.qtyMinus = container.querySelector('[data-sticky-qty-minus]');
    this.qtyPlus = container.querySelector('[data-sticky-qty-plus]');

    // Config from data attributes
    this.mainFormId = container.dataset.productFormId;
    this.pickerType = container.dataset.pickerType;
    this.pickerMin = parseFloat(container.dataset.pickerMin);
    this.pickerIncrement = parseFloat(container.dataset.pickerIncrement);
    this.isFabric = container.dataset.isFabric === 'true';
    this.addToCartText = container.dataset.addToCartText;
    this.soldOutText = container.dataset.soldOutText;

    // References to main form elements
    this.mainForm = document.getElementById(this.mainFormId);
    // Find the main ATC button within the main form, not the sticky bar's button
    this.mainATCButton = this.mainForm?.querySelector('[data-add-to-cart]');
    this.mainPicker = this.findMainPicker();

    // Initialize quantity - fabric uses picker default, non-fabric starts at 1
    this.currentQty = this.isFabric ? this.pickerMin : 1;
    this.observer = null;

    this.init();
  }

  findMainPicker() {
    // Find the main yard picker based on type
    const pickerMap = {
      'yard': 'yard-picker',
      'full-yard': 'full-yard-picker',
      'quarter-yard': 'quarter-yard-picker'
    };
    return document.querySelector(pickerMap[this.pickerType]);
  }

  init() {
    this.bindEvents();
    this.setupScrollObserver();
    this.syncWithMainPicker();
  }

  bindEvents() {
    // Listen for variant changes from main form
    document.addEventListener('theme:variant:change', this.onVariantChange.bind(this));

    // Note: Form submission is handled by the theme's CartDrawer class
    // which intercepts clicks on [data-add-to-cart] buttons

    // Quantity picker buttons
    if (this.qtyMinus) {
      this.qtyMinus.addEventListener('click', () => this.adjustQuantity(-1));
    }
    if (this.qtyPlus) {
      this.qtyPlus.addEventListener('click', () => this.adjustQuantity(1));
    }

    // Sync when main picker changes
    if (this.mainPicker) {
      const mainInput = this.mainPicker.querySelector('input[type="number"]');
      if (mainInput) {
        mainInput.addEventListener('change', () => this.syncWithMainPicker());
        mainInput.addEventListener('input', () => this.syncWithMainPicker());
      }

      // Also listen for button clicks on main picker
      const mainButtons = this.mainPicker.querySelectorAll('button');
      mainButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          setTimeout(() => this.syncWithMainPicker(), 10);
        });
      });
    }
  }

  setupScrollObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Only show sticky bar when we've scrolled PAST the main ATC button
        // (not when it's below the viewport and we haven't reached it yet)
        const rect = entry.boundingClientRect;
        const isPastButton = rect.bottom < 0;

        this.bar.classList.toggle('is-visible', isPastButton);
        this.bar.setAttribute('aria-hidden', !isPastButton);
      });
    }, { threshold: 0 });

    if (this.mainATCButton) {
      this.observer.observe(this.mainATCButton);
    }
  }

  syncWithMainPicker() {
    if (this.isFabric && this.mainPicker) {
      const mainInput = this.mainPicker.querySelector('input[type="number"]');
      if (mainInput) {
        this.currentQty = parseFloat(mainInput.value) || this.pickerMin;
        this.updateQuantityDisplay();
      }
    }
  }

  adjustQuantity(direction) {
    // For non-fabric products, use increment of 1
    const increment = this.isFabric ? this.pickerIncrement : 1;
    const minQty = this.isFabric ? this.pickerMin : 1;
    const newQty = this.currentQty + (direction * increment);

    if (newQty < minQty) return;

    // Check max from main picker if available (fabric products)
    if (this.isFabric && this.mainPicker) {
      const maxQty = parseFloat(this.mainPicker.dataset.max);
      let maxDisplayQty;

      switch (this.pickerType) {
        case 'yard':
          maxDisplayQty = maxQty / 2;
          break;
        case 'quarter-yard':
          maxDisplayQty = maxQty / 4;
          break;
        default:
          maxDisplayQty = maxQty;
      }

      if (newQty > maxDisplayQty) return;
    }

    // Update main picker (which will trigger sync back) for fabric products
    if (this.isFabric && this.mainPicker) {
      const mainInput = this.mainPicker.querySelector('input[type="number"]');
      if (mainInput) {
        mainInput.value = newQty;
        // Dispatch both events - 'input' updates the yard calculator display, 'change' updates form values
        mainInput.dispatchEvent(new Event('input', { bubbles: true }));
        mainInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.currentQty = newQty;
    this.updateQuantityDisplay();
  }

  updateQuantityDisplay() {
    // Update display value
    if (this.qtyValue) {
      this.qtyValue.textContent = this.currentQty;
    }

    if (this.isFabric) {
      // Update hidden form inputs based on picker type
      // YardPicker multiplies by 2, FullYardPicker uses value as-is, QuarterYardPicker multiplies by 4
      let cartQuantity;
      switch (this.pickerType) {
        case 'yard':
          cartQuantity = this.currentQty * 2;
          break;
        case 'quarter-yard':
          cartQuantity = this.currentQty * 4;
          break;
        default: // full-yard
          cartQuantity = this.currentQty;
      }

      if (this.quantityInput) {
        this.quantityInput.value = cartQuantity;
      }
      if (this.metresInput) {
        this.metresInput.value = this.currentQty;
      }
    } else {
      // Non-fabric: quantity is just the displayed value
      if (this.quantityInput) {
        this.quantityInput.value = this.currentQty;
      }
    }

    // Update minus button state
    const minQty = this.isFabric ? this.pickerMin : 1;
    if (this.qtyMinus) {
      this.qtyMinus.disabled = this.currentQty <= minQty;
    }

    // Check max for plus button (fabric products only)
    if (this.qtyPlus && this.isFabric && this.mainPicker) {
      const maxQty = parseFloat(this.mainPicker.dataset.max);
      let maxDisplayQty;

      switch (this.pickerType) {
        case 'yard':
          maxDisplayQty = maxQty / 2;
          break;
        case 'quarter-yard':
          maxDisplayQty = maxQty / 4;
          break;
        default:
          maxDisplayQty = maxQty;
      }

      this.qtyPlus.disabled = this.currentQty >= maxDisplayQty;
    }
  }

  onVariantChange(event) {
    const variant = event.detail.variant;

    if (variant) {
      // Update hidden variant ID
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // Update price display
      if (this.priceDisplay && typeof theme !== 'undefined' && theme.Currency) {
        this.priceDisplay.textContent = theme.Currency.formatMoney(variant.price);
      }

      // Update variant display text
      if (this.variantDisplay) {
        const span = this.variantDisplay.querySelector('span');
        if (span) span.textContent = variant.title;
      }

      // Update button state
      if (this.button) {
        this.button.disabled = !variant.available;
      }
      if (this.buttonText) {
        this.buttonText.textContent = variant.available ? this.addToCartText : this.soldOutText;
      }
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const stickyBar = document.querySelector('[data-sticky-atc-bar]');
  if (stickyBar) {
    new StickyATCBar(stickyBar);
  }
});

// Re-initialize on Shopify section reload (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const stickyBar = event.target.querySelector('[data-sticky-atc-bar]');
  if (stickyBar) {
    new StickyATCBar(stickyBar);
  }
});
